import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Circle, Rocket, X } from "lucide-react";

const STEPS = {
  coach: [
    { id: "profile", label: "Compléter mon profil (photo + position sur la carte)", to: "/tableau-de-bord", done: (u, d) => !!u.photo_url && u.lat != null },
    { id: "favorite", label: "Ajouter une salle favorite", to: "/carte", done: (u) => (u.favorites || []).length > 0 },
    { id: "booking", label: "Demander mon premier créneau", to: "/carte", done: (u, d) => d.bookings > 0 },
  ],
  room_manager: [
    { id: "room", label: "Créer ma salle avec photos", to: "/tableau-de-bord", done: (u, d) => d.rooms > 0 && d.photos > 0 },
    { id: "slot", label: "Publier mon premier créneau", to: "/tableau-de-bord", done: (u, d) => d.slots > 0 },
    { id: "push", label: "Activer les alertes push", to: "/tableau-de-bord", done: (u, d) => d.push > 0 },
  ],
  client: [
    { id: "map", label: "Explorer la carte des salles et coachs", to: "/carte", done: (u, d) => d.visited_map },
    { id: "booking", label: "Réserver ma première séance", to: "/carte", done: (u, d) => d.bookings > 0 },
    { id: "review", label: "Laisser un avis après ma séance", to: "/carte", done: (u, d) => d.reviews > 0 },
  ],
};

export default function OnboardingChecklist({ rooms = [], slots = [], bookings = [] }) {
  const { user } = useAuth();
  const [extra, setExtra] = useState({ reviews: 0, push: 0 });
  const [hidden, setHidden] = useState(() => localStorage.getItem("sc_onboarding_done") === "1");
  useEffect(() => { api.get("/onboarding/status").then((r) => setExtra(r.data)).catch(() => {}); }, [user?.id]);
  const steps = STEPS[user?.role];
  if (!steps || hidden) return null;
  const d = { rooms: rooms.length, photos: rooms.reduce((n, r) => n + (r.photos || []).length, 0), slots: slots.filter((s) => s.manager_id === user.id).length, bookings: bookings.length, ...extra, visited_map: localStorage.getItem("sc_visited_map") === "1" };
  const states = steps.map((s) => s.done(user, d));
  const doneCount = states.filter(Boolean).length;
  if (doneCount === steps.length) { localStorage.setItem("sc_onboarding_done", "1"); return null; }
  return (
    <div className="mt-6 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 relative" data-testid="onboarding-checklist">
      <button data-testid="onboarding-dismiss" onClick={() => { localStorage.setItem("sc_onboarding_done", "1"); setHidden(true); }} className="absolute top-3 right-3 p-1 rounded-full hover:bg-white" aria-label="Masquer"><X className="w-4 h-4 text-slate-500" /></button>
      <div className="flex items-center gap-2 text-emerald-800 font-bold"><Rocket className="w-5 h-5" /> Vos 3 premières actions <span className="ml-auto mr-8 text-xs font-semibold" data-testid="onboarding-progress">{doneCount}/{steps.length}</span></div>
      <div className="mt-2 h-1.5 rounded-full bg-emerald-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${(doneCount / steps.length) * 100}%` }} /></div>
      <ul className="mt-3 space-y-2">
        {steps.map((s, i) => (
          <li key={s.id} data-testid={`onboarding-step-${s.id}`} data-done={states[i]} className="flex items-center gap-2 text-sm">
            {states[i] ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-slate-300" />}
            <span className={states[i] ? "line-through text-slate-400" : "text-slate-800"}>{s.label}</span>
            {!states[i] && <Link to={s.to} className="ml-auto text-xs font-semibold text-emerald-700 underline">Faire</Link>}
          </li>
        ))}
      </ul>
    </div>
  );
}
